import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import SystemSettings from "../components/SystemSettings";
import RolePermissions from "../components/RolePermissions";
import AbacManagementPage from "../components/AbacManagementPage";
import { validatePassword, PasswordStrength, defaultRequirements } from "../utils/password-validation";
import { PasswordStrengthIndicator } from "../components/PasswordStrengthIndicator";
import { PasswordRequirements } from "../components/PasswordRequirements";
import ExternalPartnerList from '../components/ExternalPartners/ExternalPartnerList';
import UserAccountsManagement from "../components/UserAccountsManagement";
import PinSettingsCard from "../components/PinSettingsCard";
import AiUsageCostPanel from "../components/AiUsageCostPanel";
import { clearPinDevice, PIN_DEVICES_CHANGED_EVENT } from "../utils/pinDevice";
import { Eye, EyeOff } from "lucide-react";
import { calculateTimeWithCompany } from "../utils/helpers";

export default function SettingsPage() {
  const { currentUser } = useAuth();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation
  const passwordValidation = validatePassword(newPassword);

  // Check if new password is same as old password
  const isSameAsOld = oldPassword && newPassword && oldPassword === newPassword;

  // Check if confirm password matches
  const passwordsMatch = confirmPassword === "" || newPassword === confirmPassword;

  // Check which requirements are met using the validation utility
  const passwordRequirements = [
    `At least ${defaultRequirements.minLength} characters`,
    "At least one lowercase letter",
    "At least one uppercase letter",
    "At least one number",
    "At least one special character"
  ];

  const passwordMetRequirements = [
    newPassword.length >= defaultRequirements.minLength,
    /[a-z]/.test(newPassword),
    /[A-Z]/.test(newPassword),
    /\d/.test(newPassword),
    /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword)
  ];

  const [changingPassword, setChangingPassword] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    if (oldPassword === newPassword) {
      toast.error("New password cannot be the same as your current password");
      return;
    }

    // Validate password strength
    if (!passwordValidation.isValid) {
      toast.error("Password does not meet the security requirements");
      setShowPasswordRequirements(true);
      return;
    }

    setChangingPassword(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/users/me/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ oldPassword, newPassword }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.message || "Failed to change password");
        return;
      }

      // Clear form
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordRequirements(false);

      // The server revokes all PIN devices on password change — drop the
      // local deviceId and let the PIN card refresh.
      clearPinDevice();
      window.dispatchEvent(new CustomEvent(PIN_DEVICES_CHANGED_EVENT));

      toast.success("Password changed successfully");
    } catch (error) {
      console.error("Change password error:", error);
      toast.error("An error occurred while changing password");
    } finally {
      setChangingPassword(false);
    }
  };

  // Only allow admin users to see the permissions tab
  const isAdmin = currentUser?.userRole === "Admin";
  // AI spend is visible to the Account Owner as well as Admin. The API enforces the
  // same boundary with read:ai_usage_dashboard / read:ai_usage_log.
  const canViewAiUsage = isAdmin || currentUser?.userRole === "Account Owner";

  // Determine if user can change password (only Admin or users other than Director)
  const canChangePassword = currentUser?.userRole === "Admin" ||
    (currentUser?.userRole !== "Director");

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Settings</h1>

      <Tabs defaultValue="account">
        <TabsList className="bg-gray-100 p-1 rounded-full w-full sm:w-fit overflow-x-auto flex flex-nowrap no-scrollbar">
          <TabsTrigger value="account" className="rounded-full px-3 sm:px-4 py-2 data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">Account</TabsTrigger>
          {isAdmin && <TabsTrigger value="user-accounts" className="rounded-full px-3 sm:px-4 py-2 data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">User Accounts</TabsTrigger>}
          {isAdmin && <TabsTrigger value="systems" className="rounded-full px-3 sm:px-4 py-2 data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">System</TabsTrigger>}
          {isAdmin && <TabsTrigger value="permissions" className="rounded-full px-3 sm:px-4 py-2 data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">Permissions</TabsTrigger>}
          {canViewAiUsage && <TabsTrigger value="ai-usage" className="rounded-full px-3 sm:px-4 py-2 data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">AI Usage</TabsTrigger>}
          {isAdmin && <TabsTrigger value="external-partners" className="rounded-full px-3 sm:px-4 py-2 data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">Partners</TabsTrigger>}
        </TabsList>

        <TabsContent value="account" className="space-y-6 pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{currentUser?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{currentUser?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Role</p>
                    <p className="font-medium">{currentUser?.userRole}</p>
                  </div>
                  {currentUser?.dateOfBirth && (
                    <div>
                      <p className="text-sm text-muted-foreground">Date of Birth</p>
                      <p className="font-medium">{new Date(currentUser.dateOfBirth).toLocaleDateString()}</p>
                    </div>
                  )}
                  {currentUser?.startDate && (
                    <>
                      <div>
                        <p className="text-sm text-muted-foreground">Start Date</p>
                        <p className="font-medium">{new Date(currentUser.startDate).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Time with Company</p>
                        <p className="font-medium">{calculateTimeWithCompany(currentUser.startDate)}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {currentUser?.userRole !== "Director" ? (
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="oldPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="oldPassword"
                        type={showOldPassword ? "text" : "password"}
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showOldPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setShowPasswordRequirements(true);
                        }}
                        onFocus={() => setShowPasswordRequirements(true)}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {newPassword && (
                      <PasswordStrengthIndicator
                        strength={passwordValidation.strength}
                        color={passwordValidation.color}
                      />
                    )}
                    {isSameAsOld && (
                      <p className="text-sm text-red-500 mt-1">New password cannot be the same as your current password</p>
                    )}
                    {showPasswordRequirements && (
                      <PasswordRequirements
                        requirements={passwordRequirements}
                        metRequirements={passwordMetRequirements}
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {confirmPassword && !passwordsMatch && (
                      <p className="text-sm text-red-500 mt-1">Passwords do not match</p>
                    )}
                  </div>
                  <Button type="submit" disabled={changingPassword}>{changingPassword ? "Changing..." : "Change Password"}</Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Password Management</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-muted-foreground">
                    Directors cannot change their passwords in the settings area.
                    Please contact an administrator to reset your password if needed.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <PinSettingsCard />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="user-accounts" className="pt-4">
            <UserAccountsManagement />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="systems" className="pt-4">
            <SystemSettings />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="permissions" className="pt-4">
            <AbacManagementPage />
          </TabsContent>
        )}

        {canViewAiUsage && (
          <TabsContent value="ai-usage" className="pt-4">
            <AiUsageCostPanel />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="external-partners" className="pt-4">
            <ExternalPartnerList />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
